import { Injectable, Inject } from '@angular/core';
import { CoreService } from './core.service'; // Adjust import as needed
import { from, Observable } from 'rxjs';
import { ConfigurationManagerService } from './configuration-manager.service';

import * as Cesium from 'cesium';

@Injectable({
  providedIn: 'root'
})
export class BuildingTilesService {
  private tilesLoaded: Record<string, any> = {};
  private buildingPolygonsLoaded: Record<string, any> = {};
  private tileCount = 0;

  constructor(
    private core: CoreService,
    //@Inject('WINDOW') private window: Window,
  ) {
    // Keyboard shortcut for loading/removing center tile
    // this.window.document.onkeydown = (e: KeyboardEvent) => {
    //   if (e.shiftKey) {
    //     switch (e.which || e.keyCode) {
    //       case 112: // F1
    //         this.getCenterTile('add');
    //         break;
    //       // case 82: // R
    //       //   this.getCenterTile('remove');
    //       //   break;
    //       default:
    //         return;
    //     }
    //   }
    // };
  }

  loadTileForFacility(key: string, file: any, buildingMapEntity: any): Promise<any> {
    // this.log.debug('BuildingTiles.loadTileForFacility', key, file, buildingMapEntity);
    return this.loadTileData(key, file, buildingMapEntity);
  }

  loadTileXYZ(x: number, y: number, z: number, color?: any): Promise<any> {
    // this.log.debug('BuildingTiles.loadTileXYZ', x, y, z, color);
    return this.loadTile(x, y, z, color);
  }

  loadTileFromFile(key: string, file: string, color?: any): Promise<any> {
    // this.log.debug('BuildingTiles.loadTileFromFile', key, file, color);
    return fetch(file)
      .then(response => response.text())
      .then(text => {
        let data = JSON.parse(text);
        return this.loadTileData(key, data, undefined, color);
      })
      .catch(error => {
        // this.log.error('Error loading tile from file..', error);
        throw error;
      });
  }

  private loadTile(x: number, y: number, z: number, color?: any): Promise<any> {
    const viewer = this.core.getViewer();
    const key = 'dataSource' + String(x + y);

    if (
      viewer.dataSources.contains(this.tilesLoaded[key]) ||
      viewer.dataSources.contains(this.tilesLoaded[key + '-surroundings']) ||
      viewer.dataSources.contains(this.tilesLoaded[key + '-ble'])
    ) {
      return Promise.resolve();
    }
      
    const urlTemplate = ConfigurationManagerService.configSettings.buildingTilesService.loadTile.url;
    const url = urlTemplate.replace('{z}', z).replace('{x}', x).replace('{y}', y);

    // this.log.debug('Loaded tile: ' + url);
    return this.loadTileFromFile(key, url, color);
  }

  private lon2TileX(lon: number, zoom: number): number {
    return Math.floor((lon + 180) / 360 * Math.pow(2, zoom));
  }

  private lat2TileY(lat: number, zoom: number): number {
    const lata = lat * Math.PI / 180;
    return Math.floor((1 - Math.log(Math.tan(lata) + (1 / Math.cos(lata))) / Math.PI) / 2 * Math.pow(2, zoom));
  }

  private loadTileData(key: string, data: any, buildingMapEntity?: any, defaultColor?: any): Promise<any> {
    this.tilesLoaded[key + '-surroundings'] = new Cesium.GeoJsonDataSource();
    this.tilesLoaded[key + '-ble'] = new Cesium.GeoJsonDataSource();
    this.core.getViewer().dataSources.add(this.tilesLoaded[key + '-surroundings']);
    this.core.getViewer().dataSources.add(this.tilesLoaded[key + '-ble']);

    const surroundingFeatureList: any = {
      type: "FeatureCollection",
      features: []
    };
    const bleBuildingFeatureList: any = {
      type: "FeatureCollection",
      features: []
    };
    const surroundings: any[] = [];
    let bleBuilding: any;
    const bleRelations: any[] = [];

    (data.features || []).forEach((value: any) => {
      if (!(value.id in this.buildingPolygonsLoaded)) {
        if (buildingMapEntity) {
          if (value.properties && value.properties.relationId && value.properties.relationId === buildingMapEntity.getRelationId()) {
            bleBuildingFeatureList.features.push(value);
          } else if (value.id === buildingMapEntity.getBuildingId()) {
            bleBuildingFeatureList.features.push(value);
          } else {
            surroundingFeatureList.features.push(value);
          }
        } else {
          surroundingFeatureList.features.push(value);
        }
      } else {
        const entity = this.buildingPolygonsLoaded[value.id];
        if (buildingMapEntity) {
          if (entity._id === buildingMapEntity.getBuildingId()) {
            bleBuilding = entity;
          } else if (entity.properties && value.properties.relationId && entity.properties.relationId === buildingMapEntity.getRelationId()) {
            bleRelations.push(entity);
          } else {
            surroundings.push(entity);
          }
        } else {
          surroundings.push(entity);
        }
      }
    });

    // this.log.debug('Loading surroundings for ' + key + '-surroundings');
    // this.log.debug(surroundingFeatureList);

    // load surroundings
    return this.tilesLoaded[key + '-surroundings']
      .load(surroundingFeatureList, {
        stroke: Cesium.Color.TRANSPARENT,
        fill: Cesium.Color.TRANSPARENT
      })
      .then(() => {
        // this.log.debug('tileCount - ' + this.tileCount);
        this.tileCount++;

        // this.log.debug('Loaded surrondings for ' + key + '-surroundings');
        const surroundingEntities = this.tilesLoaded[key + '-surroundings'].entities._entities;

        (surroundingEntities._array || []).forEach((surroundingEntity: any) => {
          const name = surroundingEntity._id;
          this.buildingPolygonsLoaded[name] = surroundingEntity;

          if (surroundingEntity._properties.color) {
            surroundingEntity._polygon.material = Cesium.Color.fromCssColorString(surroundingEntity._properties.color.getValue());
            surroundingEntity._polygon.outlineColor = new Cesium.Color(1, 1, 1, 0);
          } else {
            if (defaultColor) {
              surroundingEntity._polygon.material = defaultColor;
            } else {
              surroundingEntity._polygon.material = Cesium.Color.LIGHTSLATEGRAY.withAlpha(0.7);
            }
            surroundingEntity._polygon.outlineColor = new Cesium.Color(0, 0, 0, 0.2);
          }

          if (surroundingEntity._properties.height !== undefined) {
            surroundingEntity._polygon._extrudedHeight = new Cesium.ConstantProperty(surroundingEntity._properties.height.getValue());
            surroundings.push(surroundingEntity);
          } else if (surroundingEntity._properties.levels !== undefined) {
            surroundingEntity._polygon._extrudedHeight = new Cesium.ConstantProperty(surroundingEntity._properties.levels.getValue() * 3);
            surroundings.push(surroundingEntity);
          } else if (surroundingEntity._properties.level !== undefined) {
            surroundingEntity._polygon._extrudedHeight = new Cesium.ConstantProperty(surroundingEntity._properties.level.getValue() * 3);
            surroundings.push(surroundingEntity);
          } else {
            surroundingEntity._polygon._extrudedHeight = new Cesium.ConstantProperty(10);
            surroundings.push(surroundingEntity);
          }
          surroundingEntity.show = true;
          surroundingEntity.building = true;
        });

        if (buildingMapEntity) {
          // this.log.debug('Set surrondings for ' + key + '-ble');
          buildingMapEntity.setSurroundings(surroundings);
        }

        // this.log.debug('Loading buildings for ' + key + '-ble');
        // this.log.debug(bleBuildingFeatureList);

        if (bleBuildingFeatureList.features.length > 0) {
          return this.tilesLoaded[key + '-ble'].load(bleBuildingFeatureList, {
            stroke: Cesium.Color.TRANSPARENT,
            fill: Cesium.Color.TRANSPARENT
          });
        }
        return Promise.resolve();
      })
      .then(() => {
        // this.log.debug('Loading buildings for ' + key + '-ble');
        const bleEntities = this.tilesLoaded[key + '-ble'].entities._entities;

        (bleEntities._array || []).forEach((bleEntity: any) => {
          const name = bleEntity._id;
          let relation;

          if (bleEntity.properties && bleEntity.properties.relationId) {
            relation = bleEntity.properties.relationId;
          }

          this.buildingPolygonsLoaded[name] = bleEntity;

          if (buildingMapEntity && name === buildingMapEntity.getBuildingId()) {
            bleBuilding = bleEntity;
          } else if (buildingMapEntity && relation && relation === buildingMapEntity.getRelationId()) {
            bleRelations.push(bleEntity);
          }
          bleEntity.show = true;
          bleEntity.building = true;
        });

        if (bleBuilding) {
          if (bleBuilding._properties.height) {
            bleBuilding._polygon.extrudedHeight = new Cesium.ConstantProperty(bleBuilding._properties.height.getValue());
          } else {
            let levels = 1;
            if (bleBuilding._properties.levels) {
              levels = bleBuilding._properties.levels.getValue();
            }
            bleBuilding._polygon._extrudedHeight = new Cesium.ConstantProperty(levels * 3);
          }
          bleBuilding._polygon.material = new Cesium.Color(1, 1, 1, 0.4);
          bleBuilding._polygon.outlineColor = new Cesium.Color(0, 0, 0, 0.3);
          buildingMapEntity.setBuildingEntity(bleBuilding);
        } else if (buildingMapEntity && !bleBuilding) {
          // // this.log.info('No wireframe found for ' + key);
          // // this.log.info(buildingMapEntity);
        }

        if (bleRelations && bleRelations.length > 0) {
          bleRelations.forEach((value: any) => {
            value._polygon.extrudedHeight = new Cesium.ConstantProperty(value._properties.height.getValue());
            value._polygon.material = new Cesium.Color(1, 1, 1, 0.4);
            value._polygon.outlineColor = new Cesium.Color(0, 0, 0, 0.3);
            buildingMapEntity.addBuildingRelationEntity(value);
          });
        }

        return;
      });
  }

  private removeTile(x: number, y: number): void {
    const viewer = this.core.getViewer();
    const data = 'dataSource' + String(x + y);

    if (viewer.dataSources.contains(this.tilesLoaded[data])) {
      viewer.dataSources.remove(this.tilesLoaded[data]);
      this.tileCount--;
    }
  }

  private getCenterTile(action: string): void {
    const viewer = this.core.getViewer();

    const windowPosition = new Cesium.Cartesian2(
      viewer.container.clientWidth / 2,
      viewer.container.clientHeight / 2
    );
    const pickRay = viewer.scene.camera.getPickRay(windowPosition);
    const pickPosition = viewer.scene.globe.pick(pickRay, viewer.scene);
    let pickPositionCartographic;
    if (typeof pickPosition !== 'undefined') {
      pickPositionCartographic = viewer.scene.globe.ellipsoid.cartesianToCartographic(pickPosition);
    } else {
      return;
    }

    const tempX = this.lon2TileX(pickPositionCartographic.longitude * (180 / Math.PI), 15);
    const tempY = this.lat2TileY(pickPositionCartographic.latitude * (180 / Math.PI), 15);

    if (action === 'add') {
      this.loadTile(tempX, tempY, 15);
    } else if (action === 'remove') {
      this.removeTile(tempX, tempY);
    }
  }
}